<template>
  <b-card no-body class="metadata-panel mb-2">
    <b-card-header class="py-1">
      <h5 class="mb-0">
        <feather-icon icon="TagIcon" /> Métadonnées
      </h5>
    </b-card-header>

    <b-card-body class="py-2">
      <b-row>
        <b-col md="6">
          <b-form-group label="Titre" label-for="md-title" :state="titleState">
            <b-form-input
              id="md-title"
              :value="value.title"
              :state="titleState"
              maxlength="200"
              placeholder="Titre de l'article tel qu'affiché côté Chatwoot"
              @input="update('title', $event)"
            />
            <b-form-invalid-feedback :state="titleState">
              Le titre est obligatoire (≤ 200 caractères).
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Catégorie" label-for="md-category" :state="categoryState">
            <b-form-select
              id="md-category"
              :value="value.category"
              :state="categoryState"
              :options="categoryOptions"
              @change="update('category', $event)"
            />
            <b-form-invalid-feedback :state="categoryState">
              Catégorie obligatoire.
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Statut Chatwoot"
            label-for="md-status"
            description="« Publié » apparaît côté help center. « Brouillon » reste caché."
          >
            <b-form-select
              id="md-status"
              :value="value.chatwoot_status || 'draft'"
              :options="statusOptions"
              @change="update('chatwoot_status', $event)"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group
        label="Repos liés (related_repos)"
        :description="relatedReposHelp"
      >
        <div class="related-repos-input">
          <b-badge
            v-for="(tag, idx) in value.related_repos"
            :key="tag + '-' + idx"
            variant="light-primary"
            class="mr-1 mb-1 related-repo-tag"
          >
            <code>{{ tag }}</code>
            <span class="ml-1 related-repo-remove" @click="removeTag(idx)">×</span>
          </b-badge>

          <vue-autosuggest
            v-model="newTag"
            :suggestions="filteredSuggestions"
            :input-props="{
              id: 'md-related-repos',
              class: 'form-control related-repo-autosuggest',
              placeholder: 'Ajouter un repo lié (Entrée pour valider)',
            }"
            :get-suggestion-value="getSuggestionValue"
            @input="onAutosuggestInput"
            @selected="onSuggestionSelected"
            @keyup.native.enter="commitNewTag"
          >
            <template slot-scope="{ suggestion }">
              <code>{{ suggestion.item.value }}</code>
              <small v-if="suggestion.item.count" class="text-muted ml-2">
                ({{ suggestion.item.count }}×)
              </small>
            </template>
          </vue-autosuggest>
        </div>

        <small v-if="tagError" class="text-danger d-block mt-1">{{ tagError }}</small>
      </b-form-group>
    </b-card-body>
  </b-card>
</template>

<script>
// Edition structuree du front-matter YAML d'une proposition de doc.
// On expose 3 champs editables (title, category, related_repos) ; les
// autres cles (slug, chatwoot_*, sync_hash, proposal_*) sont gerees par
// le backend / le sync et ne remontent pas dans l'UI en Phase 1.
//
// Modele bind via v-model :
//   value = { title, category, related_repos: [...] }
//
// L'autocompletion des related_repos consomme
// GET /admin/docs/related-repos/suggestions (deduplique + tri par freq).

import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormInvalidFeedback,
  BBadge,
} from 'bootstrap-vue'
import { VueAutosuggest } from 'vue-autosuggest'

const RELATED_REPO_RE = /^[a-z0-9._-]+\/[a-z0-9._-]+:.+$/i

export default {
  name: 'MetadataPanel',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormInvalidFeedback,
    BBadge,
    VueAutosuggest,
  },
  props: {
    value: {
      type: Object,
      required: true,
      // { title: string, category: string, related_repos: string[] }
    },
    suggestions: {
      type: Array,
      default: () => [], // [{ value, count }]
    },
    categories: {
      type: Array,
      default: () => [], // slugs autorises (vient du backend)
    },
  },
  data() {
    return {
      newTag: '',
      tagError: '',
      relatedReposHelp:
        'Format : owner/repo:prefix — ex. ubicx/buxapi:api/r_resa.js. Le skill /update-buxida-docs s\'en sert pour matcher les fichiers modifiés.',
    }
  },
  computed: {
    titleState() {
      const t = (this.value.title || '').trim()
      if (!t) return false
      if (t.length > 200) return false
      return null // pas de feedback "vert"
    },
    categoryState() {
      return this.value.category ? null : false
    },
    categoryOptions() {
      return [
        { value: '', text: '— Choisir une catégorie —', disabled: true },
        ...this.categories.map(slug => ({
          value: slug,
          text: this.formatCategoryLabel(slug),
        })),
      ]
    },
    filteredSuggestions() {
      // vue-autosuggest attend [{ data: [...] }]
      const q = (this.newTag || '').trim().toLowerCase()
      const used = new Set(this.value.related_repos || [])
      let list = (this.suggestions || []).filter(s => !used.has(s.value))
      if (q) list = list.filter(s => s.value.toLowerCase().includes(q))
      return [{ data: list.slice(0, 12) }]
    },
    statusOptions() {
      // 'archived' n'est PAS expose ici - on archive via le bouton dedie
      // dans DocsList (move physique vers _archived/), pas via une bascule
      // metadata pour eviter qu'un fichier reste en _proposals/ marque
      // archived (etat incoherent).
      return [
        { value: 'draft', text: 'Brouillon (caché côté Chatwoot)' },
        { value: 'published', text: 'Publié (visible côté Chatwoot)' },
      ]
    },
  },
  methods: {
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },
    onAutosuggestInput(value) {
      this.newTag = value
      this.tagError = ''
    },
    getSuggestionValue(s) {
      return (s && s.item && s.item.value) || ''
    },
    onSuggestionSelected(suggestion) {
      const v = (suggestion && suggestion.item && suggestion.item.value) || ''
      this.newTag = v
      this.commitNewTag()
    },
    commitNewTag() {
      const v = (this.newTag || '').trim()
      if (!v) return
      if (!RELATED_REPO_RE.test(v)) {
        this.tagError = 'Format attendu : owner/repo:prefix (ex. ubicx/buxapi:api/).'
        return
      }
      const current = Array.isArray(this.value.related_repos) ? this.value.related_repos : []
      if (current.includes(v)) {
        this.tagError = 'Ce repo est déjà dans la liste.'
        return
      }
      this.update('related_repos', current.concat([v]))
      this.newTag = ''
      this.tagError = ''
    },
    removeTag(idx) {
      const current = Array.isArray(this.value.related_repos) ? this.value.related_repos.slice() : []
      current.splice(idx, 1)
      this.update('related_repos', current)
    },
    formatCategoryLabel(slug) {
      // Convertit "comptabilite-stats" -> "Comptabilite stats" (lecture humaine).
      // La source de verite reste le slug envoye au backend.
      if (!slug) return slug
      return slug
        .split('-')
        .map(w => (w.length ? w[0].toUpperCase() + w.slice(1) : ''))
        .join(' ')
    },
  },
}
</script>

<style scoped>
.metadata-panel {
  border: 1px solid #e3e3e3;
}
.related-repos-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.related-repo-tag {
  font-size: 0.85rem;
  padding: 4px 8px;
}
.related-repo-tag code {
  background: transparent;
  color: inherit;
  font-size: 0.95em;
}
.related-repo-remove {
  cursor: pointer;
  font-weight: bold;
  opacity: 0.6;
}
.related-repo-remove:hover {
  opacity: 1;
}
.related-repos-input ::v-deep .autosuggest__results-container {
  position: relative;
  z-index: 5;
}
.related-repos-input ::v-deep .autosuggest__results {
  position: absolute;
  width: 100%;
  margin-top: 2px;
  background: #fff;
  border: 1px solid #d8d6de;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  max-height: 240px;
  overflow-y: auto;
}
.related-repos-input ::v-deep .autosuggest__results ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.related-repos-input ::v-deep .autosuggest__results li {
  padding: 6px 10px;
  cursor: pointer;
}
.related-repos-input ::v-deep .autosuggest__results li:hover,
.related-repos-input ::v-deep .autosuggest__results .autosuggest__results-item--highlighted {
  background: #f4f5f7;
}
.related-repos-input ::v-deep .related-repo-autosuggest {
  min-width: 240px;
  flex: 1;
}
</style>
